import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { API, ym } from "@/App";
import api from "@/api/axios";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { YearPicker } from "@/components/YearPicker";
import { MonthPicker } from "@/components/month-picker";
import {
  CheckIcon,
  DollarSignIcon,
  IndianRupeeIcon,
  PlusIcon,
  SplitIcon,
  TargetIcon,
  TrendingUpIcon,
} from "lucide-react";
import { Label } from "@/components/ui/label";

export default function OpexManager() {
  const currentYear = new Date().getFullYear();
  const [fyy, setFyy] = useState(`${currentYear}-${currentYear + 1}`);
  const [opexList, setOpexList] = useState([]);
  const [projects, setProjects] = useState([]);
  const [form, setForm] = useState({
    projectId: "",
    monthYear: ym(new Date()),
    officeExpense: "",
    partnerRemuneration: "",
    mode: "auto",
    // category : "",
  });
  const [editing, setEditing] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const fetchOpex = async () => {
    const res = await api.get(`${API}/opex`, { params: { fyy } });
    setOpexList(res.data.data || []);
  };

  const fetchProjects = async () => {
    const res = await api.get(`${API}/projects?status=ACTIVE`);
    setProjects(res.data || []);
  };

  useEffect(() => {
    fetchOpex();
    fetchProjects();
  }, [fyy]);

  const handleOpenChange = (open) => {
    setIsDialogOpen(open);
    if (!open) {
      setEditing(null);
      setForm({
        projectId: "",
        monthYear: ym(new Date()),
        overheadAmount: "",
        mode: "auto",
      });
    }
  };

  const saveOpex = async () => {
    if (form.mode === "auto") {
      await api.post(`${API}/projects/opcost`, {
        month: form.monthYear,
        officeExpense: form.officeExpense,
        partnerRemuneration: form.partnerRemuneration,
      });
    } else {
      if (editing) {
        await api.put(`${API}/opex/${editing}`, form);
      } else {
        await api.post(`${API}/opex`, form);
      }
    }

    handleOpenChange(false);
    fetchOpex();
  };

  const removeOpex = async (id) => {
    await api.delete(`${API}/opex/${id}`);
    setConfirmDelete(null);
    fetchOpex();
  };

  return (
    <div className="p-6 space-y-6">
      <Card className="rounded-2xl shadow-md">
        <CardContent className="p-6 space-y-4">
          <div className="flex items-center gap-4">
            <YearPicker value={fyy} onChange={setFyy} />
            <Button onClick={fetchOpex}>Refresh</Button>
          </div>

          <Dialog open={isDialogOpen} onOpenChange={handleOpenChange}>
            <DialogTrigger asChild>
              <Button
                className="mt-4 bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2 px-4 rounded-lg shadow-sm transition-colors duration-200"
                onClick={() => {
                  setEditing(null);
                  setForm({
                    projectId: "",
                    monthYear: ym(new Date()),
                    overheadAmount: "",
                    mode: "auto",
                  });
                  setIsDialogOpen(true);
                }}
              >
                <PlusIcon className="w-4 h-4 mr-2" />
                Add Operational Expense
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-md bg-white rounded-lg shadow-xl">
              <DialogHeader className="border-b border-gray-100 pb-4">
                <DialogTitle className="text-xl font-semibold text-gray-900 flex items-center gap-2">
                  <TrendingUpIcon className="w-5 h-5 text-blue-600" />
                  {editing
                    ? "Edit Operational Expense"
                    : "Add New Operational Expense"}
                </DialogTitle>
              </DialogHeader>

              <div className="space-y-6 py-4">
                {/* Distribution Mode */}
                <div className="space-y-2">
                  <Label
                    htmlFor="distribution-mode"
                    className="text-sm font-medium text-gray-700"
                  >
                    Distribution Mode *
                  </Label>
                  <Select
                    value={form.mode}
                    onValueChange={(v) => setForm({ ...form, mode: v })}
                  >
                    <SelectTrigger
                      id="distribution-mode"
                      className="w-full border-gray-300 focus:border-blue-500 focus:ring-blue-500"
                    >
                      <SelectValue placeholder="Select distribution mode" />
                    </SelectTrigger>
                    <SelectContent className="bg-white border border-gray-200 shadow-lg">
                      <SelectItem
                        value="manual"
                        className="flex items-center gap-2"
                      >
                        <TargetIcon className="w-4 h-4" />
                        <span>Per Project</span>
                      </SelectItem>
                      <SelectItem
                        value="auto"
                        className="flex items-center gap-2"
                      >
                        <SplitIcon className="w-4 h-4" />
                        <span>Auto: Equal Split</span>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-gray-500">
                    Choose how to distribute this expense across projects
                  </p>
                </div>

                {/* Project Selection - Conditionally Rendered */}
                {form.mode === "manual" && (
                  <div className="space-y-2">
                    <Label
                      htmlFor="project-select"
                      className="text-sm font-medium text-gray-700"
                    >
                      Project *
                    </Label>
                    <Select
                      value={form.projectId}
                      onValueChange={(v) => setForm({ ...form, projectId: v })}
                    >
                      <SelectTrigger
                        id="project-select"
                        className="w-full border-gray-300 focus:border-blue-500 focus:ring-blue-500"
                      >
                        <SelectValue placeholder="Select a project" />
                      </SelectTrigger>
                      <SelectContent className="bg-white border border-gray-200 shadow-lg max-h-60">
                        {projects.map((p) => (
                          <SelectItem
                            key={p._id}
                            value={p._id}
                            className="flex items-center gap-2"
                          >
                            <div className="w-2 h-2 rounded-full bg-blue-500"></div>
                            <span className="truncate">{p.name}</span>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                {/* Month Picker */}
                <div className="space-y-2">
                  <Label
                    htmlFor="month-year"
                    className="text-sm font-medium text-gray-700"
                  >
                    Period *
                  </Label>
                  <MonthPicker
                    id="month-year"
                    placeholder="Select month and year"
                    value={form.monthYear}
                    onChange={(e) => setForm({ ...form, monthYear: e })}
                    className="w-full border-gray-300 focus:border-blue-500 focus:ring-blue-500"
                    hideIcon={true}
                  />
                </div>

                {/* Amount Input */}
                {/* <div className="space-y-2">
                  <Label
                    htmlFor="amount"
                    className="text-sm font-medium text-gray-700"
                  >
                    Amount *
                  </Label>
                  <div className="relative">
                    <IndianRupeeIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                    <Input
                      id="amount"
                      placeholder="0.00"
                      type="number"
                      step="0.01"
                      min="0"
                      value={form.overheadAmount}
                      onChange={(e) =>
                        setForm({ ...form, overheadAmount: e.target.value })
                      }
                      className="pl-10 border-gray-300 focus:border-blue-500 focus:ring-blue-500"
                    />
                  </div>
                  <p className="text-xs text-gray-500">
                    Enter the total operational expense amount
                  </p>
                </div> */}

                {/* Office Expense */}
                <div className="space-y-2">
                  <Label
                    htmlFor="officeExpense"
                    className="text-sm font-medium text-gray-700"
                  >
                    Office Expense *
                  </Label>

                  <div className="relative">
                    <IndianRupeeIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                    <Input
                      id="officeExpense"
                      placeholder="0.00"
                      type="number"
                      step="0.01"
                      min="0"
                      value={form.officeExpense}
                      onChange={(e) =>
                        setForm({ ...form, officeExpense: e.target.value })
                      }
                      className="pl-10 border-gray-300 focus:border-blue-500 focus:ring-blue-500"
                    />
                  </div>
                </div>

                {/* Partner Remuneration */}
                <div className="space-y-2">
                  <Label
                    htmlFor="partnerRemuneration"
                    className="text-sm font-medium text-gray-700"
                  >
                    Partner Remuneration *
                  </Label>

                  <div className="relative">
                    <IndianRupeeIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                    <Input
                      id="partnerRemuneration"
                      placeholder="0.00"
                      type="number"
                      step="0.01"
                      min="0"
                      value={form.partnerRemuneration}
                      onChange={(e) =>
                        setForm({
                          ...form,
                          partnerRemuneration: e.target.value,
                        })
                      }
                      className="pl-10 border-gray-300 focus:border-blue-500 focus:ring-blue-500"
                    />
                  </div>
                </div>
              </div>

              <DialogFooter className="border-t border-gray-100 pt-4 flex gap-3">
                <Button
                  onClick={saveOpex}
                  className="flex-1 bg-blue-600 hover:bg-blue-700 text-white font-medium shadow-sm transition-colors duration-200"
                >
                  <CheckIcon className="w-4 h-4 mr-2" />
                  {editing ? "Update Expense" : "Save Expense"}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Project</TableHead>
                <TableHead>Month</TableHead>
                <TableHead>Amount</TableHead>
                <TableHead>Category</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {opexList.map((row) => (
                <TableRow key={row._id}>
                  <TableCell>
                    {projects.find((p) => p._id === row.projectId)?.name || "-"}
                  </TableCell>
                  <TableCell>{row.monthYear}</TableCell>
                  {/* <TableCell>{row.overheadAmount}</TableCell> */}
                  <TableCell>{Math.round(row.overheadAmount)}</TableCell>
                  <TableCell>{row.category || "N/A"}</TableCell>
                  <TableCell className="space-x-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        setEditing(row._id);
                        setForm({
                          projectId: row.projectId,
                          monthYear: row.monthYear,
                          overheadAmount: row.overheadAmount,
                          mode: "manual",
                        });
                        setIsDialogOpen(true);
                      }}
                    >
                      Edit
                    </Button>
                    <Button
                      size="sm"
                      variant="destructive"
                      onClick={() => setConfirmDelete(row._id)}
                    >
                      Delete
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={!!confirmDelete}
        onOpenChange={() => setConfirmDelete(null)}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirm Delete</DialogTitle>
          </DialogHeader>
          <p>Are you sure you want to delete this OPEX entry?</p>
          <div className="flex justify-end gap-2 mt-4">
            <Button variant="outline" onClick={() => setConfirmDelete(null)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => removeOpex(confirmDelete)}
            >
              Delete
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
